import React, { useState, useEffect, useRef, useCallback } from 'react';
import Loading from '../Common/Loading';

// --- Interfaces ---
interface QuestionStat {
    perc: number;
    total: number;
    display_text: string;
}

interface GridRow {
    lesson_code: string;
    lesson_name: string;
    avg_score: number;
    questions: Record<string, QuestionStat>;
}

// --- Configuration Constants ---
const MAX_QUESTIONS = 100;
const COLUMNS = Array.from({ length: MAX_QUESTIONS }, (_, i) => String(i + 1).padStart(3, '0'));

const COLORS = {
    headBg: '#c7ddfd',
    headText: '#2859ac',
    border: '#80a0ff',
    rowOdd: '#f4faff',
    rowEven: '#fff',
    rowSelected: 'orange',
    failBg: 'red',
    failText: 'black'
};

const EL_Lessonperformance: React.FC = () => {
    const API_BASE = process.env.REACT_APP_DOCUMENT_LINK || 'https://staging.sysadmin.elearnoncloud.com/';

    // --- Date Helpers ---
    const getFormattedDate = useCallback((date: Date): string => {
        return date.toISOString().split('T')[0];
    }, []);

    const getYesterdayDate = (): string => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return getFormattedDate(yesterday);
    };

    // --- State Management ---
    const [country, setCountry] = useState<string>('Australia');
    const [dics, setDics] = useState<string[]>([]);
    const [isDicMode, setIsDicMode] = useState<boolean>(true);
    const [selectedDic, setSelectedDic] = useState<string>('--Select--');
    const [lessons, setLessons] = useState<string[]>([]);
    const [selectedLesson, setSelectedLesson] = useState<string>('');
    const [manualLesson, setManualLesson] = useState<string>('');
    const [fromDate, setFromDate] = useState<string>(getYesterdayDate());
    const [toDate, setToDate] = useState<string>(getFormattedDate(new Date()));
    const [threshold, setThreshold] = useState<number>(70);
    const [famWise, setFamWise] = useState<boolean>(true);
    const [gridData, setGridData] = useState<GridRow[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [hasSearched, setHasSearched] = useState<boolean>(false);
    const [selectedRowCode, setSelectedRowCode] = useState<string | null>(null);
    const [headerRowHeight, setHeaderRowHeight] = useState<number>(0);

    const headerRowRef = useRef<HTMLTableRowElement>(null);

    // --- Global Style Effect Cleanup ---
    useEffect(() => {
        const originalBg = document.body.style.backgroundColor;
        document.body.style.backgroundColor = '#fff';
        return () => {
            document.body.style.backgroundColor = originalBg;
        };
    }, []);

    // --- Calculate Sticky Sub-Header Dynamic Gap ---
    useEffect(() => {
        if (headerRowRef.current) {
            setHeaderRowHeight(headerRowRef.current.getBoundingClientRect().height);
        }
    }, [gridData, hasSearched]);

    // --- Dynamic Style Presets ---
    const styles = {
        thBase: {
            backgroundColor: COLORS.headBg,
            color: COLORS.headText,
            border: `1px solid ${COLORS.border}`,
            padding: '4px 6px',
            textAlign: 'center' as const,
            position: 'sticky' as const,
            top: 0,
            zIndex: 3,
            whiteSpace: 'nowrap' as const
        },
        thSubBase: {
            backgroundColor: COLORS.headBg,
            color: COLORS.headText,
            border: `1px solid ${COLORS.border}`,
            padding: '4px 6px',
            textAlign: 'center' as const,
            position: 'sticky' as const,
            top: headerRowHeight > 0 ? `${headerRowHeight}px` : '31px',
            zIndex: 3,
            whiteSpace: 'nowrap' as const
        },
        tdBase: {
            borderRight: `1px solid ${COLORS.border}`,
            borderLeft: 'none',
            borderTop: 'none',
            borderBottom: 'none',
            padding: '8px',
            textAlign: 'center' as const,
            whiteSpace: 'nowrap' as const,
            fontSize: '13px'
        }
    };

    // --- Data Fetching Operations ---
    useEffect(() => {
        const fetchDics = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE}api/q_perf_get_dics?country=${country}`);
                if (!res.ok) throw new Error('Failed to fetch DICs data');
                const json = await res.json();
                setDics(json.dics || []);
                setSelectedDic('--Select--');
                setLessons([]);
                setGridData([]);
                setHasSearched(false);
            } catch (err) {
                console.error(err);
                alert('Error loading DIC records. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        fetchDics();
    }, [country, API_BASE]);

    useEffect(() => {
        if (selectedDic === '--Select--') return;

        const fetchLessons = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE}api/l_perf_get_lessons?dic_name=${selectedDic}`);
                if (!res.ok) throw new Error('Failed to fetch lessons metadata');
                const json = await res.json();
                const list = json.lessons || [];
                setLessons(list);
                setSelectedLesson(list.length > 0 ? list[0] : '');
            } catch (err) {
                console.error(err);
                alert('Error loading Lessons maps.');
            } finally {
                setLoading(false);
            }
        };

        if (isDicMode) {
            fetchLessons();
        }
    }, [selectedDic, isDicMode, API_BASE]);

    const clearGrid = () => {
        setGridData([]);
        setHasSearched(false);
        setSelectedRowCode(null);
    };

    // --- Action Handlers ---
    const handleGo = async () => {
        const targetLesson = isDicMode ? selectedLesson : manualLesson.trim();
        if (!targetLesson) {
            alert('Please provide a Lesson Code.');
            return;
        }

        setLoading(true);
        setHasSearched(true);
        setSelectedRowCode(null);
        setGridData([]);

        try {
            const res = await fetch(`${API_BASE}api/l_perf_get_data`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    country,
                    mode: isDicMode ? 'dic' : 'lsn',
                    dic_code: selectedDic,
                    lesson_code: targetLesson,
                    from_date: fromDate,
                    to_date: toDate
                })
            });

            if (!res.ok) throw new Error('Data grid retrieval failed');
            const json = await res.json();

            if (json.error) {
                alert(json.error);
            } else {
                setGridData(json.results || []);
            }
        } catch (err) {
            console.error(err);
            alert('Error generating report results.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        window.location.href = '/SysAdmin';
    };

    const handleCellClick = (e: React.MouseEvent, lessonCode: string, qnNo: string) => {
        e.stopPropagation();
        setSelectedRowCode(lessonCode);

        const w = 1100;
        const h = 800;
        const left = window.screen.width / 2 - w / 2;
        const top = window.screen.height / 2 - h / 2;

        const url = famWise
            ? `/SUPPORT/EL_QuestionInsights?DICCode=${isDicMode ? selectedDic : ''}&Qnno=${qnNo}&Title=${lessonCode}&Country=${country}&from=Lessonperformance&Fromlsndate=${fromDate}&Tolsndate=${toDate}`
            : `/LessonBrowser/QuestionViewer?Qnno=${qnNo}&Title=${lessonCode}&Country=${country}&from=Lessonperformance`;

        window.open(url, '_blank', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
    };

    return (
        <div style={{ fontFamily: 'Arial', fontSize: '13pt', margin: 0, overflowX: 'hidden' }}>
            {loading && <Loading />}

            {/* Title Banner */}
            <div style={{ backgroundColor: 'teal', color: 'white', textAlign: 'center', fontSize: '16pt', padding: '10px' }}>
                LESSON PERFORMANCE ANALYSIS
            </div>

            {/* Filter Dashboard panel */}
            <div style={{ padding: '10px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', backgroundColor: '#f9f9f9', borderBottom: '1px solid #ccc' }}>
                <span>Country :</span>
                <select style={{ fontFamily: 'Arial', fontSize: '14pt', color: 'blue', padding: '2px' }} value={country} onChange={(e) => setCountry(e.target.value)}>
                    <option value="Australia">Australia</option>
                    <option value="Canada">Canada</option>
                </select>

                <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input type="radio" checked={isDicMode} onChange={() => {
                        setIsDicMode(true);
                        clearGrid();
                    }} />
                    Dicfile :
                </label>
                <select
                    style={{ fontFamily: 'Arial', fontSize: '14pt', color: 'blue', padding: '2px', width: '200px' }}
                    value={selectedDic}
                    onChange={(e) => {
                        setSelectedDic(e.target.value);
                        clearGrid();
                    }}
                    disabled={!isDicMode}
                >
                    <option>--Select--</option>
                    {dics.map(d => <option key={d} value={d}>{d}</option>)}
                </select>

                <select
                    style={{ fontFamily: 'Arial', fontSize: '14pt', color: 'blue', padding: '2px', width: '140px' }}
                    value={selectedLesson}
                    onChange={(e) => {
                        setSelectedLesson(e.target.value);
                        clearGrid();
                    }}
                    disabled={!isDicMode}
                >
                    {lessons.map(l => <option key={l} value={l}>{l}</option>)}
                </select>

                <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '6px' }}>
                    <input type="radio" checked={!isDicMode} onChange={() => {
                        setIsDicMode(false);
                        clearGrid();
                    }} />
                    Lessoncode
                </label>
                <input
                    type="text"
                    style={{ fontFamily: 'Arial', fontSize: '14pt', padding: '2px', width: '130px' }}
                    value={manualLesson}
                    onChange={(e) => {
                        setManualLesson(e.target.value);
                        clearGrid();
                    }}
                    disabled={isDicMode}
                />

                <span style={{ marginLeft: '6px' }}>From :</span>
                <input type="date" style={{ fontFamily: 'Arial', fontSize: '11pt', padding: '2px' }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />

                <span>TO :</span>
                <input type="date" style={{ fontFamily: 'Arial', fontSize: '11pt', padding: '2px' }} value={toDate} onChange={(e) => setToDate(e.target.value)} />

                <select style={{ fontFamily: 'Arial', fontSize: '14pt', color: 'blue', height: '28px' }} value={threshold} onChange={(e) => setThreshold(Number(e.target.value))}>
                    {[100, 90, 80, 70, 60, 50, 40, 30, 20, 10].map(v => <option key={v} value={v}>{v}</option>)}
                </select>

                <button style={{ fontFamily: 'Arial', fontSize: '12pt', cursor: 'pointer', padding: '2px 10px' }} onClick={handleGo}>Go</button>

                <label style={{ fontSize: '14px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={famWise} onChange={(e) => setFamWise(e.target.checked)} />
                    {' '}Family-wise Wrong Answers
                </label>

                <button style={{ fontFamily: 'Arial', fontSize: '14pt', cursor: 'pointer', padding: '4px 15px', marginLeft: 'auto' }} onClick={handleClose}>CLOSE</button>
            </div>

            {/* Outer Header Section */}
            {hasSearched && !loading && gridData.length > 0 && (
                <div style={{
                    backgroundColor: COLORS.headBg,
                    color: COLORS.headText,
                    textAlign: 'center',
                    padding: '5px 0',
                    fontFamily: 'Arial',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    borderBottom: `1px solid ${COLORS.border}`
                }}>
                    Question Nos And Marks
                </div>
            )}

            {/* Matrix Data Grid Container */}
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '700px', width: '100%' }}>
                {hasSearched && gridData.length === 0 && !loading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>No data available for this lesson in the selected date range.</div>
                ) : (
                    <table style={{ borderCollapse: 'collapse', width: 'max-content', fontSize: '13px', fontFamily: 'Arial', borderBottom: "1px solid #80a0ff" }}>
                        <thead>
                            <tr ref={headerRowRef}>
                                <th style={{ ...styles.thBase, minWidth: '30px' }} rowSpan={2}>#</th>
                                <th style={{ ...styles.thBase, textAlign: 'left', minWidth: '100px' }} rowSpan={2}>LessonCode</th>
                                <th style={{ ...styles.thBase, textAlign: 'left', minWidth: '220px' }} rowSpan={2}>LessonName</th>
                                <th style={{ ...styles.thBase, minWidth: '80px' }} rowSpan={2}>Av lsnScore</th>
                                <th colSpan={MAX_QUESTIONS} style={{ ...styles.thBase, textAlign: 'center' }}>
                                    Question Nos And Marks
                                </th>
                            </tr>
                            <tr>
                                {COLUMNS.map(col => (
                                    <th key={col} style={{ ...styles.thSubBase, minWidth: '48px' }}>
                                        {parseInt(col, 10)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {gridData.map((row, idx) => {
                                const isSelected = selectedRowCode === row.lesson_code;
                                const rowBg = isSelected ? COLORS.rowSelected : (idx % 2 === 0 ? COLORS.rowEven : COLORS.rowOdd);
                                const avgIsFail = row.avg_score < threshold;

                                return (
                                    <tr
                                        key={row.lesson_code}
                                        style={{ backgroundColor: rowBg, cursor: 'pointer' }}
                                        onClick={() => setSelectedRowCode(row.lesson_code)}
                                    >
                                        <td style={{ ...styles.tdBase, color: '#555' }}>{idx + 1}</td>
                                        <td style={{ ...styles.tdBase, textAlign: 'left', fontWeight: 'bold' }}>{row.lesson_code}</td>
                                        <td style={{ ...styles.tdBase, textAlign: 'left' }}>{row.lesson_name}</td>
                                        <td style={{
                                            ...styles.tdBase,
                                            fontWeight: 'bold',
                                            backgroundColor: avgIsFail ? COLORS.failBg : 'transparent',
                                            color: avgIsFail ? COLORS.failText : '#000'
                                        }}>
                                            {row.avg_score}
                                        </td>
                                        {COLUMNS.map(col => {
                                            const qnStat = row.questions[col];
                                            const isFail = qnStat && qnStat.perc < threshold;
                                            return (
                                                <td
                                                    key={col}
                                                    style={{
                                                        ...styles.tdBase,
                                                        backgroundColor: isFail ? COLORS.failBg : 'transparent',
                                                        color: isFail ? COLORS.failText : '#000',
                                                        cursor: qnStat ? 'pointer' : 'default'
                                                    }}
                                                    onClick={(e) => { if (qnStat) handleCellClick(e, row.lesson_code, col); }}
                                                >
                                                    {qnStat ? qnStat.display_text : ''}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default EL_Lessonperformance;